/**
 * The raw material the day's board is built from.
 *
 * Events are generated rather than hand-written now, because a board has to be
 * *over*-subscribed to mean anything and nobody is going to hand-author eight
 * events a day for seven days. What is authored here is the shape of an event:
 * who books it, who it reflects on, how big it tends to be, and what it sounds
 * like.
 *
 * `owner` is the department whose reputation rides on the room. It is the whole
 * reason the board can hurt you for work you never touched — a Staff
 * Appreciation Dinner that nobody set is the Kitchen's evening ruined, and the
 * Kitchen does not care that you were in the ballroom doing a wedding.
 */

/** Hotel-plausible names. No real people, no real companies. */
export const CLIENTS = [
  'Ashford & Vance',
  'Meridian Group',
  'Halloran',
  'Coast Rotary',
  'Pellerin Trust',
  'Whitcombe Partners',
  'The Brennan Foundation',
  'Ardmore Mutual',
  'Sattler & Roe',
  'Kingsbridge Association',
  'Delacroix',
  'Varden Industries',
];

export const EVENT_TYPES = [
  {
    id: 'reception',
    name: 'Reception',
    owner: 'banquets',
    covers: [40, 90],
    linens: ['ivory', 'white'],
    staging: ['Dance floor to remain clear'],
    weight: 3,
    notes: [
      'Mother of the bride will count the chairs. She will count them twice.',
      'Photographer wants the room at six. The room is not due at six.',
    ],
  },
  {
    id: 'corporate',
    name: 'Quarterly',
    owner: 'frontdesk',
    covers: [24, 64],
    linens: ['black', 'white'],
    staging: ['Dance floor to remain clear', 'Aisle on the ballroom doors side'],
    weight: 3,
    notes: [
      'Nobody will look at the room and everybody will have an opinion.',
      'Desk has promised them a view. There is no view.',
    ],
  },
  {
    id: 'gala',
    name: 'Charity Gala',
    owner: 'management',
    covers: [64, 96],
    linens: ['burgundy', 'black'],
    staging: ['Dance floor to remain clear', 'Riser for the auction'],
    weight: 2,
    notes: ['The AGM will walk this room personally. Twice.'],
  },
  {
    id: 'rehearsal',
    name: 'Rehearsal Dinner',
    owner: 'banquets',
    covers: [32, 72],
    linens: ['ivory', 'burgundy'],
    staging: ['Dance floor to remain clear'],
    weight: 2,
    notes: ['Kitchen has already said the count is wrong. It is not.'],
  },
  {
    id: 'luncheon',
    name: 'Luncheon',
    owner: 'frontdesk',
    covers: [24, 56],
    linens: ['hunter', 'white'],
    staging: ['Dance floor to remain clear'],
    weight: 2,
    notes: ['In and out in ninety minutes, which is nobody’s idea of a lunch.'],
  },
  {
    id: 'staff-dinner',
    name: 'Staff Appreciation Dinner',
    owner: 'kitchen',
    covers: [40, 80],
    linens: ['white', 'hunter'],
    staging: ['Dance floor to remain clear'],
    weight: 2,
    notes: ['Chef has cooked for this one himself. Do not make him regret it.'],
  },
  {
    id: 'showcase',
    name: 'Motor Club Showcase',
    owner: 'valet',
    covers: [24, 48],
    linens: ['black', 'ivory'],
    staging: ['Dance floor to remain clear', 'Keep the doors run clear'],
    weight: 1,
    notes: ['Valet have been talking about this for a month.'],
  },
  {
    id: 'linen-show',
    name: 'Design Showcase',
    owner: 'housekeeping',
    covers: [24, 48],
    linens: ['ivory', 'burgundy', 'hunter'],
    staging: ['Dance floor to remain clear'],
    weight: 1,
    notes: ['Housekeeping picked the linen. Housekeeping will be watching it.'],
  },
  {
    id: 'trades',
    name: 'Trades Association Dinner',
    owner: 'engineering',
    covers: [40, 72],
    linens: ['hunter', 'black'],
    staging: ['Dance floor to remain clear', 'Riser for the presentation'],
    weight: 1,
    notes: ['Half the room will be engineers, and all of them will look up.'],
  },
];

/**
 * Table blocks a room diagram can call for, as columns by rows.
 *
 * Kept to shapes that read as a plan at a glance. A 5x1 is a long run down one
 * end and looks like a mistake until you have set one; a 4x3 is most of the
 * room and is meant to feel like too much, because sometimes it is.
 */
export const BLOCKS = [
  { cols: 2, rows: 2, weight: 3 },
  { cols: 3, rows: 2, weight: 4 },
  { cols: 2, rows: 3, weight: 3 },
  { cols: 3, rows: 3, weight: 2 },
  { cols: 4, rows: 2, weight: 2 },
  { cols: 5, rows: 1, weight: 1 },
  { cols: 4, rows: 3, weight: 1 },
];

/**
 * How long work takes, in game minutes.
 *
 * Measured, not guessed: a clean six-round, forty-eight-chair Ashford run with
 * the cart is 114 game minutes walked end to end, which is six supply trips and
 * sixty placements. These two numbers reproduce that to within three minutes,
 * and they are the only thing the board uses to decide how far to
 * over-subscribe a shift. If the cart's capacity or the property's length ever
 * changes, re-run `npm run playthrough` and put the new numbers here.
 */
export const EFFORT = {
  tripMinutes: 18,
  itemMinutes: 0.15,
  /** Getting to the board, reading it and walking to storage once. */
  overheadMinutes: 8,
};

/** How much more work is posted than a shift can hold. The board must not fit. */
export const OVERSUBSCRIBE = { min: 1.6, max: 2.0 };

/** How many shortfalls an event rolls. */
export const SHORTFALL_COUNT = [1, 3];

export default EVENT_TYPES;
